import { useState } from 'react'
import type { Me } from '@stagepost/web/api/types'
import { AuthPanel, type SignUpWording } from '@stagepost/web/auth/AuthPanel'
import { HeroRoom } from './HeroRoom'
import { MARKETING_FACES, MARKETING_FACE_STYLE } from './faces'
import { Proof, Reframe } from './Argument'
import { Benefits, Install, Layers, Price, Record, Teams, Trust } from './Sections'
import { SecondaryAction } from './atoms'
import { RouteLink } from '@stagepost/web/app/RouteLink'
import { FaceProvider, Mark, PrimaryAction } from '@stagepost/web/components/primitives'
import '@stagepost/web/styles/public-page.css'
import './marketing.css'

/**
 * How stagepost.io asks a visitor in. Signing up is instant, so the page says
 * early access, which is true, and never "invite only" or "request access": a
 * visitor who lands straight in their account would see through either at once.
 */
const EARLY_ACCESS: SignUpWording = {
  title: 'Get early access',
  switchToSignup: 'New here? Get early access',
  submit: 'Create account',
}

/**
 * What a logged-out visitor sees.
 *
 * Kept to what gets someone to sign up rather than a full explanation of the
 * product. The deeper "how it works" detail (the three kinds of conversation,
 * the full comparison table, what belongs in the room) isn't needed to decide
 * whether to sign up, so it stays off this page. Framing
 * rules that are still easy to get wrong, and are settled:
 *
 *   - **Not conflict avoidance.** Worktrees already stop agents overwriting
 *     each other. Stagepost is a shared room, not a lock. An earlier draft
 *     argued "two agents will overwrite each other" and it was simply wrong.
 *   - **Not a plugin install.** The entry model is a web app.
 *   - **Never claim this replaces agent teams or subagents.** The honest
 *     picture is three worktrees, each running its own team with its own
 *     lead, sharing a room. That claim lives in the Layers section.
 *
 * One deliberate departure from the original design: it sends every call to
 * action off to `app.stagepost.io`, because it was drawn as a separate
 * marketing deployment. Here the page and the app are one origin, so the
 * buttons scroll to `#start` and the closing call to action is the sign-up
 * form itself.
 *
 * The order below is the argument, and it is worth stating because it was not
 * always this one. Reframe says what the product is for, Proof shows both ends
 * of it working, and everything after that elaborates. The page used to open on
 * four benefit cards and bury its central claim two thirds of the way down, in
 * row two of a four-row list styled exactly like the rows around it.
 */
export function Marketing({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [mode, setMode] = useState<'signup' | 'login'>('signup')

  const startSignup = () => setMode('signup')
  const startLogin = () => setMode('login')

  return (
    /* One provider for the page, so a name wears one face all the way down it.
       `db-schema` is Elton John in the panel above the fold and has to still be
       Elton John in the claims tree three thousand pixels below; two maps, or a
       provider around only the hero, is how that drifts. `HeroRoom` keeps its
       own as well, because it is rendered on its own in tests and a panel that
       goes grey when nothing wraps it is a panel that will one day ship grey. */
    <FaceProvider style={MARKETING_FACE_STYLE} faces={MARKETING_FACES}>
    <div style={{ position: 'relative', width: '100%', minHeight: 'var(--app-height)', overflowX: 'hidden' }}>
      <div className="sp-marketing-glow" />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header onSignUp={startSignup} onLogIn={startLogin} />
        <Hero onSignUp={startSignup} onLogIn={startLogin} />

        <Reframe />
        <Proof />
        <Benefits />
        <Layers />
        <Teams />
        <Record />
        <Trust />
        <Install />
        <Price onSignUp={startSignup} />

        <Closing mode={mode} onModeChange={setMode} onSignedIn={onSignedIn} />
        <Footer />
      </div>
    </div>
    </FaceProvider>
  )
}

/**
 * Where the section links go, in one list.
 *
 * The header and the footer offer the same five, and two hand-kept copies of
 * one nav is how they drift. The labels are the fix for a pair that read well
 * and navigated badly: "What it remembers" is a nice phrase that tells nobody
 * what is behind it, and the two things it hid, a repository's status and an
 * organisation's lessons, are among the most useful parts of the product.
 */
const NAV: ReadonlyArray<{ label: string; to: string }> = [
  { label: 'How it works', to: '#proof' },
  { label: 'Status & lessons', to: '#record' },
  { label: 'Guide', to: '/guide' },
  { label: 'FAQ', to: '#trust' },
  { label: 'Price', to: '#price' },
]

function NavLinks({ style }: { style: React.CSSProperties }) {
  return (
    <>
      {NAV.map((entry) =>
        entry.to.startsWith('#') ? (
          <a key={entry.label} href={entry.to} style={style}>
            {entry.label}
          </a>
        ) : (
          <RouteLink key={entry.label} to={entry.to} style={style}>
            {entry.label}
          </RouteLink>
        ),
      )}
    </>
  )
}

function Header({ onSignUp, onLogIn }: { onSignUp: () => void; onLogIn: () => void }) {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        backdropFilter: 'blur(18px)',
        background: 'rgba(0,0,0,.66)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="sp-page-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark />
          <span className="sp-page-wordmark" style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.035em' }}>
            stagepost
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <nav className="sp-marketing-nav-links" aria-label="Sections">
          <NavLinks style={navLink} />
        </nav>
        <div className="sp-page-actions" style={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
          <a href="#start" onClick={onLogIn} style={{ ...navLink, fontWeight: 600, color: 'var(--text-soft)' }}>
            Log in
          </a>
          <PrimaryAction href="#start" onClick={onSignUp} compact>
            Get early access
          </PrimaryAction>
        </div>
      </div>
    </header>
  )
}

const navLink = {
  fontSize: 13.5,
  fontWeight: 500,
  color: 'var(--text-body)',
} as const

function Hero({ onSignUp, onLogIn }: { onSignUp: () => void; onLogIn: () => void }) {
  return (
    <div className="sp-page-section sp-hero">
      <div style={{ display: 'flex', gap: 'clamp(28px,5vw,64px)', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 420px', minWidth: 300 }}>
          <div
            className="sp-hero-badge"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              padding: '6px 13px 6px 9px',
              borderRadius: 20,
              background: 'rgba(110,168,254,.1)',
              border: '1px solid var(--accent-edge)',
            }}
          >
            <span
              aria-hidden="true"
              style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }}
            />
            <span
              style={{
                font: '500 11px var(--font-machine)',
                letterSpacing: '.14em',
                textTransform: 'uppercase',
                color: 'var(--accent-bright)',
              }}
            >
              One room per repository
            </span>
          </div>

          {/* "Collisions" was the last word on the site still claiming
              something the product spent a release correcting. Nothing ever
              collides: every agent is in its own worktree, and what actually
              happens is that two branches head for one file and whoever merges
              second inherits it. Every string a person reads now says overlap,
              and a headline contradicting the rest of the page is the one that
              teaches a reader to discount all of it. "Rework" is what the page
              argues underneath anyway, twice: more of what your agents build
              ships instead of getting redone. */}
          <h1 className="sp-hero-title">
            Run more agents.
            <br />
            <span style={{ color: 'var(--text-meta)' }}>
              Ship without
              <br />
              the rework.
            </span>
          </h1>

          <p className="sp-hero-lead">
            Give every agent its own worktree, that part is already right. Stagepost is the room that keeps all
            of them, and everyone running one, in sync.
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <PrimaryAction href="#start" onClick={onSignUp}>
              Get early access
            </PrimaryAction>
            <SecondaryAction href="#start" onClick={onLogIn}>
              Log in
            </SecondaryAction>
          </div>

          <div className="sp-hero-claims">
            {['works with any agent', 'ships more work'].map((claim, index) => (
              <span key={claim} style={{ display: 'contents' }}>
                {index > 0 && (
                  <span aria-hidden="true" style={{ font: '400 11.5px var(--font-machine)', color: 'var(--text-faint)' }}>
                    ·
                  </span>
                )}
                <span style={{ font: '400 11.5px var(--font-machine)', color: 'var(--text-dim)' }}>{claim}</span>
              </span>
            ))}
          </div>
        </div>

        <div style={{ flex: '1 1 420px', minWidth: 300 }}>
          <LiveCue />
          <HeroRoom />
        </div>
      </div>
    </div>
  )
}

/**
 * That the room above is running, and that you can type in it.
 *
 * The demonstration is the strongest thing on this page and the only invitation
 * to touch it used to be `try typing @phase-plan`, set in 10.5px dim mono in the
 * bottom-right corner of the panel, below the fold on a phone. A visitor who
 * misses it reads the whole page as a screenshot with an animation on it.
 *
 * It sits outside `HeroRoom` on purpose. The room is a faithful drawing of a
 * product surface and "this is a demo" is a fact about the marketing page, not
 * about the room; putting it inside would mean the panel lies about itself.
 */
function LiveCue() {
  return (
    <div className="sp-live-cue">
      <span aria-hidden="true" className="sp-live-dot" />
      <span style={{ font: '500 10.5px var(--font-machine)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--accent-bright)' }}>
        Live
      </span>
      <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
        This room is running. Type in it, or name{' '}
        <span style={{ font: '500 12px var(--font-machine)', color: 'var(--text-body)' }}>@phase-plan</span>{' '}
        and watch the scope change.
      </span>
    </div>
  )
}

function Closing({
  mode,
  onModeChange,
  onSignedIn,
}: {
  mode: 'signup' | 'login'
  onModeChange: (mode: 'signup' | 'login') => void
  onSignedIn: (me: Me) => void
}) {
  return (
    <section
      id="start"
      className="sp-page-section"
      style={{ paddingBottom: 'clamp(64px,9vw,120px)' }}
    >
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'clamp(48px,7vw,88px)', textAlign: 'center' }}>
        <h2
          style={{
            margin: '0 auto 20px',
            fontSize: 'clamp(28px,3.8vw,48px)',
            lineHeight: 1.08,
            fontWeight: 800,
            letterSpacing: '-.04em',
            maxWidth: 760,
            textWrap: 'balance',
          }}
        >
          Keep the worktrees. Add the room.
        </h2>
        <p
          style={{
            margin: '0 auto 30px',
            fontSize: 'clamp(15px,1.4vw,17.5px)',
            lineHeight: 1.6,
            color: 'var(--text-dim)',
            maxWidth: 540,
            textWrap: 'pretty',
          }}
        >
          One room per repo for what everyone needs to know. Every agent keeps its own worktree, its own goal, its
          own subagents, and its own conversation with you.
        </p>
        <p style={{ margin: '0 auto 16px', fontSize: 13.5, color: 'var(--text-dim)' }}>Free during early access.</p>
        <AuthPanel mode={mode} onModeChange={onModeChange} onSignedIn={onSignedIn} wording={EARLY_ACCESS} />
      </div>
    </section>
  )
}

function Footer() {
  const link = { fontSize: 13, color: 'var(--text-body)' } as const
  return (
    <footer style={{ borderTop: '1px solid var(--border)', background: 'rgba(255,255,255,.015)' }}>
      <div className="sp-page-footer-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark size={20} color="var(--text-body)" />
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-.03em', color: 'var(--text-body)' }}>
            stagepost
          </span>
        </div>
        <div
          style={{
            fontFamily: 'var(--font-human)',
            fontStyle: 'italic',
            fontSize: 13.5,
            color: 'var(--text-dim)',
          }}
        >
          A staging post was where couriers handed messages onward and swapped horses.
        </div>
        <div style={{ flex: 1 }} />
        <nav style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }} aria-label="Elsewhere">
          <NavLinks style={link} />
          {/* The FAQ two links along says the room is treated as private data.
              A page that makes that claim and offers nothing behind it is
              asking to be taken on faith, and both sign-in providers expect a
              published policy from anything taking their accounts. */}
          <RouteLink to="/privacy" style={link}>
            Privacy
          </RouteLink>
          <RouteLink to="/terms" style={link}>
            Terms
          </RouteLink>
        </nav>
      </div>
    </footer>
  )
}
